import type { Composer, IntlDateTimeFormats } from 'vue-i18n';
import { createI18n } from 'vue-i18n';
import * as vuetifyLocales from 'vuetify/locale';
import { useAuth } from '@/composables/useAuth';
import dayjs, { dateLocaleLoaders } from '@/dayjs';

const { authUser } = useAuth();
const permittedLocales: string[] = [
  'en',
  'fr',
  'es',
  'it',
  'pt_BR',
  'el',
  'de',
] as const;
export type PermittedLocale = typeof permittedLocales[number];
const defaultLanguage: PermittedLocale = 'en';

export const availableLanguages: { locale: PermittedLocale; name: string }[] = [
  { locale: 'en', name: 'English' },
  { locale: 'fr', name: 'Français' },
  { locale: 'es', name: 'Español' },
  { locale: 'it', name: 'Italiano' },
  { locale: 'pt_BR', name: 'Português (Brasil)' },
  { locale: 'el', name: 'Ελληνικά' },
  { locale: 'de', name: 'Deutsch' },
];

const datetimeFormats: IntlDateTimeFormats = {
  en: {
    short: {
      day: 'numeric',
      month: 'short',
    },
    long: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    short_day: {
      weekday: 'short',
    },
    short_with_day: {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    },
    long_day: {
      weekday: 'long',
    },
    long_date_with_day: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_short_day: {
      weekday: 'short',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_time: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
    long_date_with_day_and_time: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
  },
  fr: {
    short: {
      day: 'numeric',
      month: 'short',
    },
    long: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    short_day: {
      weekday: 'short',
    },
    short_with_day: {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    },
    long_day: {
      weekday: 'long',
    },
    long_date_with_day: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_short_day: {
      weekday: 'short',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_time: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
    long_date_with_day_and_time: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
  },
  es: {
    short: {
      day: 'numeric',
      month: 'short',
    },
    long: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    short_day: {
      weekday: 'short',
    },
    short_with_day: {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    },
    long_day: {
      weekday: 'long',
    },
    long_date_with_day: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_short_day: {
      weekday: 'short',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_time: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
    long_date_with_day_and_time: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
  },
  it: {
    short: {
      day: 'numeric',
      month: 'short',
    },
    long: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    short_day: {
      weekday: 'short',
    },
    short_with_day: {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    },
    long_day: {
      weekday: 'long',
    },
    long_date_with_day: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_short_day: {
      weekday: 'short',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_time: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
    long_date_with_day_and_time: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
  },
  pt_BR: {
    short: {
      day: 'numeric',
      month: 'short',
    },
    long: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    short_with_day: {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    },
    short_day: {
      weekday: 'short',
    },
    long_day: {
      weekday: 'long',
    },
    long_date_with_day: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_short_day: {
      weekday: 'short',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_time: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
    long_date_with_day_and_time: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
  },
  el: {
    short: {
      day: 'numeric',
      month: 'short',
    },
    long: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    short_day: {
      weekday: 'short',
    },
    short_with_day: {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    },
    long_day: {
      weekday: 'long',
    },
    long_date_with_day: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_short_day: {
      weekday: 'short',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_time: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
    long_date_with_day_and_time: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
  },
  de: {
    short: {
      day: 'numeric',
      month: 'short',
    },
    long: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    short_day: {
      weekday: 'short',
    },
    short_with_day: {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    },
    long_day: {
      weekday: 'long',
    },
    long_date_with_day: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_short_day: {
      weekday: 'short',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    },
    long_date_with_time: {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
    long_date_with_day_and_time: {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    },
  },
};

// One chunk per locale file: only English (the fallback) and the user's own locale are fetched.
const localeLoaders = import.meta.glob<{ default: Record<string, unknown> }>('../lang/*.json');
const loadedMessageKeys = new Set<string>();

/**
 * Fetch a locale's messages (merged with Vuetify's own strings) and register them. Call this
 * before switching to that locale, otherwise its strings fall back to English.
 */
export async function loadLocaleMessages(composer: Pick<Composer, 'setLocaleMessage'>, locale: string): Promise<void> {
  const file = locale.replace('-', '_');
  // vue-i18n falls back from `pt-BR` to `pt`, so the Brazilian file is registered under `pt`
  const messageKey = file === 'pt_BR'
    ? 'pt'
    : file;
  const loader = localeLoaders[`../lang/${file}.json`];
  if (loadedMessageKeys.has(messageKey) || loader == null) {
    return;
  }

  const { default: localeMessages } = await loader();
  const vuetifyLocale = (vuetifyLocales as Record<string, unknown>)[messageKey];
  if (vuetifyLocale == null) {
    return;
  }

  composer.setLocaleMessage(messageKey, {
    ...localeMessages,
    $vuetify: {
      ...vuetifyLocale as Record<string, unknown>,
      ...localeMessages.$vuetify as Record<string, unknown>,
    },
  });
  loadedMessageKeys.add(messageKey);
}

function isLocalePermitted(locale: string): boolean {
  return permittedLocales.includes(locale);
}

function getPreferredLocaleOrUseBrowserSetting(): PermittedLocale {
  if (authUser?.preferred_language && isLocalePermitted(authUser?.preferred_language)) {
    return authUser.preferred_language;
  }
  if (navigator?.languages.length && isLocalePermitted(navigator.languages[0])) {
    const [locale] = navigator.languages;
    return locale;
  }

  if (Object.keys(navigator).includes('userLanguage')) {
    const { userLanguage } = navigator as unknown as { userLanguage: string };
    if (isLocalePermitted(userLanguage)) {
      return userLanguage;
    }
  }

  if (navigator.language && isLocalePermitted(navigator.language)) {
    return navigator.language;
  }

  if (Object.keys(navigator).includes('browserLanguage')) {
    const { browserLanguage } = navigator as unknown as { browserLanguage: string };
    if (isLocalePermitted(browserLanguage)) {
      return browserLanguage;
    }
  }

  return defaultLanguage;
}

export async function setDateLocale(locale: string): Promise<void> {
  try {
    const dateLocale = locale.toLowerCase().replace('_', '-');
    if (dateLocale !== 'en') {
      const loadLocale = dateLocaleLoaders[dateLocale];
      if (!loadLocale) {
        throw new Error(`No Day.js locale for ${dateLocale}`);
      }
      await loadLocale();
    }
    dayjs.locale(dateLocale);
  }
  catch (e) {
    dayjs.locale('en');
    console.warn(`Cannot load date locale ${locale}`, e);
  }
}

export async function initI18n() {
  const locale = getPreferredLocaleOrUseBrowserSetting();
  let realLocale = locale;
  if (locale.includes('_')) {
    realLocale = locale.replace('_', '-');
  }
  setDateLocale(realLocale);
  const i18n = createI18n({
    legacy: false,
    locale: realLocale,
    fallbackLocale: defaultLanguage,
    messages: {},
    datetimeFormats,
    missingWarn: false,
    fallbackWarn: false,
  });
  await Promise.all([
    loadLocaleMessages(i18n.global, defaultLanguage),
    loadLocaleMessages(i18n.global, realLocale),
  ]);
  return i18n;
}
